interface ActivityIndicatorProps {
  status: 'online' | 'away' | 'offline' | null | undefined;
  lastSeen?: number;
  showLabel?: boolean;
  size?: 'sm' | 'md';
}

const statusConfig = {
  online: {
    icon: '●',
    color: 'text-green-500',
    bgColor: 'bg-green-500',
    label: 'Online',
  },
  away: {
    icon: '◐',
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500',
    label: 'Away',
  },
  offline: {
    icon: '○',
    color: 'text-gray-500',
    bgColor: 'bg-gray-500',
    label: 'Offline',
  },
};

/**
 * ActivityIndicator - Shows real-time presence status
 * 
 * ● Online (green) - Active in last 5 minutes
 * ◐ Away (yellow) - Active 5-30 minutes ago
 * ○ Offline (gray) - Inactive for 30+ minutes
 */
export function ActivityIndicator({
  status,
  lastSeen,
  showLabel = true,
  size = 'md',
}: ActivityIndicatorProps) {
  // Don't render if no status (manual entries or not logged in)
  if (!status) return null;

  const config = statusConfig[status];
  if (!config) return null;

  // Calculate away time for display
  const getAwayTime = () => {
    if (status !== 'away' || !lastSeen) return '';
    const minutes = Math.round((Date.now() - lastSeen) / 60000);
    if (minutes < 1) return '';
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      return ` (${hours}h)`;
    }
    return ` (${minutes}m)`;
  };

  const sizeClasses = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <span className={`${config.color} ${sizeClasses} flex items-center gap-1`}>
      <span className="leading-none">{config.icon}</span>
      {showLabel && (
        <span>
          {config.label}
          {getAwayTime()}
        </span>
      )}
    </span>
  );
}

/**
 * ActivityDot - Just the colored dot, no label
 * Use this for compact displays like timeline rows
 */
export function ActivityDot({
  status,
  size = 'md',
}: {
  status: 'online' | 'away' | 'offline' | null | undefined;
  size?: 'sm' | 'md';
}) {
  if (!status) return null;

  const config = statusConfig[status];
  if (!config) return null;

  const dotSize = size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5';

  return (
    <span
      className={`${config.bgColor} ${dotSize} rounded-full inline-block`}
      title={config.label}
    />
  );
}

export default ActivityIndicator;
